// Should handle most of the basic CRUD operations

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGridPremium, GridActionsCellItem, GRID_CHECKBOX_SELECTION_COL_DEF, useGridApiRef, useKeepGroupedColumnsHidden, gridExpandedSortedRowIdsSelector, GRID_AGGREGATION_FUNCTIONS, isAutogeneratedRow } from '@mui/x-data-grid-premium';
import SettingsIcon from '@mui/icons-material/Settings';
import PrintIcon from '@mui/icons-material/Print';
import SkeletonLoadingOverlay from '../components/SkeletonLoadingOverlay';
import { Badge, Snackbar } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { LocationPositionPickerDialogForSampleDataGrid } from 'components/SampleLocationPicker/LocationPickerDialog';
import axios from 'axios';
import moment from 'moment';
import { parseSampleName, sampleNameComparator } from '../utils/utils';

export default function SampleDataGrid({api, columns, AddForm, onSelectionModelDidChange, getPrintLabel, actions = [], initialState, isRowSelectable, ...props}) {
  let [searchParams, setSearchParams] = useSearchParams();
  const apiRef = useGridApiRef();
  const [data, setData] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const [columnOrder, setColumnOrder] = useState(null);
  const columnsWithStoredWidths = useMemo(
    () => columns.map(column => columnWidths[column.field] ? {...column, width: columnWidths[column.field]} : column),
    [columns, columnWidths]
  );
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set([]),
  });
  const selectedIds = rowSelectionModel.type === 'exclude'
    ? (data || []).map(row => row.id).filter(id => !rowSelectionModel.ids.has(id))
    : [...rowSelectionModel.ids];
  const handleSelectionModelChange = (newSelectionModel) => {
    setRowSelectionModel(newSelectionModel)
    onSelectionModelDidChange?.([...newSelectionModel.ids])
  }
  useEffect(() => {
    if (!data?.length || !searchParams.get("id"))
      return

    const id_query = Number(searchParams.get("id"))
    if (!Number.isInteger(id_query)) {
      setSearchParams()
      return
    }

    if (!data.some(row => row.id === id_query)) {
      setSearchParams()
      return
    }

    let attempts = 0
    let frame
    const jumpToSelectedRow = () => {
      const rowIndex = gridExpandedSortedRowIdsSelector(apiRef).findIndex(id => id === id_query)
      if (rowIndex >= 0) {
        setRowSelectionModel({type: 'include', ids: new Set([id_query])})
        if (apiRef.current.scrollToIndexes({rowIndex})) {
          setSearchParams()
          return
        }
      }
      if (attempts++ < 120)
        frame = requestAnimationFrame(jumpToSelectedRow)
    }
    frame = requestAnimationFrame(jumpToSelectedRow)
    return () => {
      cancelAnimationFrame(frame)
    }
  }, [data, searchParams, setSearchParams])
  const [showForm, setShowForm] = useState() // index, row. index = -1 is reserved for create
  const [message, setMessage] = useState()
  // Create
  const handleCreate = (newRow) => {
    const max_id = data == null ? 0 : data.reduce((cumVal, curVal) => Math.max(cumVal, curVal.id), -Infinity) // gets max id in data
    const temp_id = Math.floor(100000 * Math.random()) + max_id + 1 // +1 guarantees different from data, random makes unlikely collison with another add
    axios.post(api.create, newRow)
      .then(({data : {id}}) => setData(prev => prev.map(x => x.id === temp_id ? {...x, id} : x)))
      .catch(() => setMessage({message: "Error creating new row", open: true}))
    setData(prev => [...prev, {id: temp_id, ...newRow}])
  }
  // Read
  useEffect(() => {
    const loadStuff = () => {
      axios.get(api.read).then(({data: {data: newData}}) => {
        setData(previousData => {
          const previousRows = new Map(previousData?.map(row => [row.id, row]));
          return newData?.map?.(row => previousRows.get(row.id)?.sequence !== undefined
            ? {...row, sequence: previousRows.get(row.id).sequence}
            : row);
        })
        if (newData?.length === 0)
          setShowForm({index: -1}) // force add new row if empty data
      })
    };
    loadStuff();
    const fetchInterval = setInterval(loadStuff, 10000);
    return () => clearInterval(fetchInterval);
  }, [api, apiRef]);
  // Lazy load columns (i.e. big data like sequence) when they are visible and in the viewport
  const lazyLoadColumns = columns.filter(column => column.lazy_load);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(initialState?.columns?.columnVisibilityModel || {})
  const lazyFetchedAt = useRef(new Map())
  const loadVisibleLazyColumns = useCallback((firstRowIndex, lastRowIndex, visibilityModel = columnVisibilityModel) => {
    if (!data)
      return;
    const now = Date.now();
    const ids = gridExpandedSortedRowIdsSelector(apiRef).slice(firstRowIndex, lastRowIndex + 1);
    lazyLoadColumns
      .filter(column => visibilityModel[column.field] !== false)
      .forEach(column => {
        const idsToLoad = ids.filter(id => now - (lazyFetchedAt.current.get(`${column.field}:${id}`) || 0) >= 60000);
        if (idsToLoad.length === 0)
          return;
        idsToLoad.forEach(id => lazyFetchedAt.current.set(`${column.field}:${id}`, now));
        axios.post(column.lazy_load, {ids: idsToLoad}).then(({data: {data: lazyData}}) => {
          const valuesById = new Map(lazyData.map(row => [row.id, row[column.field]]));
          setData(prev => prev?.map(row => valuesById.has(row.id) ? {...row, [column.field]: valuesById.get(row.id)} : row));
        });
      });
  }, [columnVisibilityModel, data, lazyLoadColumns, apiRef]);
  useEffect(() => {
    if (lazyLoadColumns.length === 0)
      return;
    const unsubscribeRows = apiRef.current.subscribeEvent('renderedRowsIntervalChange', ({firstRowIndex, lastRowIndex}) => {
      loadVisibleLazyColumns(firstRowIndex, lastRowIndex);
    });
    const unsubscribeColumns = apiRef.current.subscribeEvent('columnVisibilityModelChange', model => {
      setColumnVisibilityModel(model);
      loadVisibleLazyColumns(0, gridExpandedSortedRowIdsSelector(apiRef).length - 1, model);
    });
    return () => {
      unsubscribeRows();
      unsubscribeColumns();
    };
  }, [lazyLoadColumns, loadVisibleLazyColumns, apiRef]);
  // Update
  const processRowUpdate = async (updatedRow, originalRow) => {
    var updates = Object.fromEntries(
      Object.entries(updatedRow)
        .filter(([key, value]) => !key.startsWith('_') && (value !== originalRow?.[key]))
        .map(([key, value]) => [key, value === undefined ? null : value])
      )
    axios.post(api.update, {id: updatedRow.id, ...updates})
      .catch((error) => {
        setData(prev => prev.map(x => x.id === originalRow.id ? originalRow : x))
        setMessage({message: `Failed to update row: ${error.message}`, open: true})
      })
    return updatedRow
  }
  // Delete
  const handleDelete = (id) => {
    axios.post(api.delete, {id})
      .catch((error) => {
        setMessage({message: `Failed to delete row: ${error.message}`, open: true})
      })
    setData(prev => prev.filter(x => x.id !== id));
  }
  // Ubiquitinylate
  const handleUbiquitinylate = (ids) => {
    axios.post(api.ubiquitinylate, {ids})
      .then(({status}) => status === 200 && setMessage({message: `Ubiquitinylated ${ids.length} sample${ids.length === 1 ? '' : 's'}`, open: true}))
      .catch((error) => {
        setMessage({message: `Failed to ubiquitinylate sample${ids.length === 1 ? '' : 's'}: ${error.message}`, open: true})
      })
    setData(prev => prev.map(x => ids.includes(x.id) ? {...x, ubiquitin: true} : x));
  }
  // Print labels
  const handlePrintLabels = (ids) => {
    if (!getPrintLabel)
      return
    if (!ids || ids.length === 0)
      return
    const dataToPrint = ids.map(x => getPrintLabel(data.find(y => y.id === x)))
    const stringToPrint = ids.length === 1 ?
      dataToPrint.map(x => x.join("\n"))[0]
      :
      dataToPrint.map(x => x.join("\t")).join("\n")
    if (navigator.clipboard) {
      navigator.clipboard.writeText(stringToPrint)
      setMessage({message: `${ids.length > 1 ? "Labels" : "Label"} copied to clipboard`, open: true})
    }
    else
      setMessage({message: "Can't access clipboard", open: true})
  }
  // Export as TSV
  const handleCopy = (ids) => {
    if (!ids || ids.length === 0)
      return
    const stringToPrint = apiRef.current.getDataAsCsv({getRowsToExport: () => ids, delimiter: "\t"})
    if (navigator.clipboard) {
      navigator.clipboard.writeText(stringToPrint)
      setMessage({message: "TSV copied to clipboard", open: true})
    }
    else
      setMessage({message: "Can't access clipboard", open: true})
  }
  const ACTIONS_LABELS = (params) => {
    if (!getPrintLabel)
      return []
    var data_to_print
    if (selectedIds.length === 0)
      data_to_print = [params.id]
    else if (selectedIds.includes(params.id))
      data_to_print = selectedIds
    else
      data_to_print = []
    return [
      <GridActionsCellItem
        icon={<Badge badgeContent={(data_to_print.length > 1) && data_to_print.length}><PrintIcon /></Badge>}
        onClick={() => handlePrintLabels(data_to_print)}
        label={`Print Label${data_to_print.length > 1 ? "s" : ""}`}
        showInMenu
        disabled={data_to_print.length === 0}
        />,
      <GridActionsCellItem
        icon={<Badge badgeContent={(data_to_print.length > 1) && data_to_print.length}><ContentCopyIcon /></Badge>}
        onClick={() => handleCopy(data_to_print)}
        label="Copy"
        showInMenu
        disabled={data_to_print.length === 0}
        />,
    ]
  }
  // For hiding column of the group
  const initialStateWithGroupedColsHidden = useKeepGroupedColumnsHidden({apiRef, initialState});
  const gridColumns = [
    ...columnsWithStoredWidths,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    {
      field: 'actions',
      renderHeader: () => <SettingsIcon fontSize="small" color="action" />,
      type: 'actions',
      width: 50,
      getActions: params => [
        <GridActionsCellItem icon={<AddIcon />} onClick={() => setShowForm({index: -1})} label="New" showInMenu disabled={!AddForm} />,
        ...actions.map((action, index) => ({action, index})).filter(({action}) => action.shouldShow ? action.shouldShow(params) : !isAutogeneratedRow(params.row)).map(({action, index}) => {
          if (action.multiple) {
            return (
              <GridActionsCellItem
                key={index}
                icon={<Badge badgeContent={selectedIds.length}>{action.icon}</Badge>}
                onClick={() => setShowForm({
                  index,
                  row_id: (action.multiple === "only") ? selectedIds : (selectedIds.length > 0 ? selectedIds : [params.id])
                })} // preserve order of selection
                label={action.label}
                showInMenu
                disabled={(action.multiple === "only") && (selectedIds.length === 0)}
              />
            )
          }
          else
            return (
              <GridActionsCellItem
                key={index}
                icon={action.icon}
                onClick={() => setShowForm({index, row_id: params.id})}
                label={action.label}
                showInMenu
              />
            )
        }),
        !isAutogeneratedRow(params.row) && <GridActionsCellItem icon={<DeleteIcon />} onClick={() => handleDelete(params.id)} label="Discard" showInMenu />,
        !isAutogeneratedRow(params.row) && <GridActionsCellItem icon={<Badge badgeContent={selectedIds.length}><DeleteIcon /></Badge>} onClick={() => selectedIds.forEach(id => handleDelete(id))} label="Discard Selected" showInMenu disabled={selectedIds.length === 0} />,
        ...(api.ubiquitinylate && !isAutogeneratedRow(params.row) ? [<GridActionsCellItem icon={<Badge badgeContent={(selectedIds.length > 1) && selectedIds.length}><DeleteIcon /></Badge>} onClick={() => handleUbiquitinylate(selectedIds.length > 0 ? selectedIds : [params.id])} label="Ubiquitinylate" showInMenu disabled={(selectedIds.length > 0) && !selectedIds.includes(params.id)} />] : []),
        ...(!isAutogeneratedRow(params.row) ? ACTIONS_LABELS(params) : []),
      ]
    }
  ];
  const orderedGridColumns = columnOrder
    ? [
        ...columnOrder.map(field => gridColumns.find(column => column.field === field)).filter(Boolean),
        ...gridColumns.filter(column => !columnOrder.includes(column.field)),
      ]
    : gridColumns;
  return <>
    <DataGridPremium
      apiRef={apiRef}
      initialState={{density: "compact", ...initialStateWithGroupedColsHidden}}
      rows={data || []}
      loading={data == null}
      sx={{border: "none"}}
      columns={orderedGridColumns}
      hideFooter
      checkboxSelection
      disableRowSelectionOnClick={selectedIds.length === 0}
      onRowSelectionModelChange={handleSelectionModelChange}
      onColumnWidthChange={(params, event, details) => {
        setColumnWidths(widths => ({...widths, [params.colDef.field]: params.width}));
      }}
      onColumnOrderChange={() => {
        setColumnOrder(apiRef.current.getAllColumns().map(column => column.field));
      }}
      rowSelectionModel={rowSelectionModel}
      processRowUpdate={processRowUpdate}
      slots={{
        loadingOverlay: SkeletonLoadingOverlay,
      }}
      cellSelection
      aggregationFunctions={{
        ...GRID_AGGREGATION_FUNCTIONS,
        consensus: {
          apply: (params) => {
            if (params.values.length === 0)
              return "";
            const valueCounts = params.values.reduce((counts, value) => {
              const _value = value || ""
              counts[_value] = (counts[_value] || 0) + 1;
              return counts;
            }, {});
            const maxCount = Math.max(...Object.values(valueCounts));
            const mostFrequentValues = Object.keys(valueCounts).filter(value => valueCounts[value] === maxCount);
            return mostFrequentValues.length === 1 ? mostFrequentValues[0] : "";
          },
          label: 'consensus',
          columnTypes: ['string'],
        },
      }}
      isRowSelectable={isRowSelectable ? params => isRowSelectable(
          params,
          [...apiRef.current.getSelectedRows().keys()]
            .map(id => apiRef.current.getRow(id))
            .filter(Boolean),
        )
      : undefined}
      {...props}
    />
    {AddForm && <AddForm open={showForm?.index === -1} onClose={() => setShowForm(null)} onSubmit={newRow => handleCreate(newRow)} />}
    {actions.map((action, index) => (
      <action.Form
        key={index}
        open={showForm?.index === index}
        row={(showForm?.index === index) && (() => {
          const getFormRow = id => {
            const row = data.find(x => x.id === id);
            if (row)
              return row;
            const groupNode = apiRef.current.getRowNode(id);
            if (!groupNode || groupNode.type !== 'group')
              return undefined;
            const childIds = apiRef.current.getRowGroupChildren({
              groupId: id,
              applyFiltering: false,
            });
            return {
              id,
              isGroup: true,
              groupingField: groupNode.groupingField,
              groupingKey: groupNode.groupingKey,
              childIds,
              children: childIds
                .map(childId => data.find(x => x.id === childId))
                .filter(Boolean),
            };
          };
          return Array.isArray(showForm.row_id)
            ? showForm.row_id.map(getFormRow).filter(Boolean)
            : getFormRow(showForm.row_id);
        })()}
        onClose={() => setShowForm(null)}
        setData={setData}
        showMessage={(message) => setMessage({message, open: true})}
        // Don't pass api.update. Instead, use a hook to pass it to forms that need it.
      />
    ))}
    {message && <Snackbar autoHideDuration={6000} {...message} onClose={() => setMessage(prev => ({...prev, open: false}))} />}
  </>;
}

export function GRID_SAMPLE_LOCATION_COL_DEF() {
  const [containers, setContainers] = useState([])
  useEffect(() => {
    axios.get("/api/wetlab/container/read").then(({data : {data}}) => setContainers(data))
  }, []);
  return ([
    {
      field: 'location_box',
      headerName: "Box",
      width: 120,
      editable: true,
      type: "singleSelect",
      valueOptions: containers.map(x => ({value: x.id, label: x.name})),
      valueFormatter: value => containers && value && containers.find(x => x.id === value)?.name
    },
    {
      field: 'location_pos',
      headerName: "Position",
      width: 80,
      editable: true,
      type: "number",
      renderEditCell: (params) => (
        <LocationPositionPickerDialogForSampleDataGrid {...params} />
      ),
    }
  ])
}

export const GRID_SAMPLE_EXPERIMENT_COL_DEF = {
  field: 'experiment',
  headerName: 'Experiment',
  width: 80,
  editable: true,
  sortComparator: sampleNameComparator,
  valueGetter: (value, row) => {
    if (value)
      return value
    const parsed = parseSampleName(row.name)
    return parsed.experiment ? `SPS${parsed.experiment}` : undefined
  }
}

export const GRID_SAMPLE_DATE_COL_VAL = (field, format = "M/D/YY") => ({
  valueGetter: value => value && moment(value),
  valueFormatter: value => value?.format?.('YYYY-MM-DD HH:mm:ss'),
  valueSetter: (value, row) => ({...row, [field]: value && moment(value).toISOString()}),
  renderCell: ({value}) => value?.format?.(format),
})